import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { isMaterialDebugEnabled, materialDebugLog } from '../../utils/materialDebug';

/**
 * Opt-in diagnostic (?debugMaterials=1). Must be rendered INSIDE <Canvas> so it
 * can read the R3F root scene. Polls `scene.environment` for a few seconds after
 * mount because drei's <Environment> sets it asynchronously once the HDR loads —
 * this captures whether/when the environment map actually binds to the scene
 * (metallic materials reflect `scene.environment`).
 *
 * Remove once the "metallic material missing" investigation is resolved.
 */
export function EnvProbe() {
  const scene = useThree((state) => state.scene);
  const gl = useThree((state) => state.gl);

  useEffect(() => {
    if (!isMaterialDebugEnabled()) return;

    const describe = (label: string) => {
      const env = scene.environment as THREE.Texture | null;
      const bg = scene.background;
      materialDebugLog(`EnvProbe ${label}`, {
        hasEnvironment: Boolean(env),
        environmentIsTexture: env instanceof THREE.Texture,
        environmentMapping: env?.mapping,
        environmentColorSpace: (env as THREE.Texture | null)?.colorSpace,
        environmentIntensity: (scene as unknown as { environmentIntensity?: number })
          .environmentIntensity,
        backgroundType:
          bg instanceof THREE.Texture
            ? 'texture'
            : bg instanceof THREE.Color
              ? `color(#${bg.getHexString()})`
              : String(bg),
        toneMapping: gl.toneMapping,
        toneMappingExposure: gl.toneMappingExposure,
        outputColorSpace: gl.outputColorSpace,
      });
    };

    describe('mount');

    // Poll for a few seconds; drei sets scene.environment after the async HDR load.
    let ticks = 0;
    const id = window.setInterval(() => {
      ticks += 1;
      describe(`t+${ticks * 500}ms`);
      if (ticks >= 8 || scene.environment) {
        window.clearInterval(id);
        if (scene.environment) describe('environment-bound');
      }
    }, 500);

    return () => window.clearInterval(id);
  }, [scene, gl]);

  return null;
}
